/**
 * Maps imageIds back to the bytes of the instance they came from.
 *
 * This is what makes bounded memory possible. A decoded frame can always be
 * reproduced from its source, so nothing needs to keep a second copy of the
 * pixels anywhere — not in the heap, not in the image cache beyond its budget.
 *
 * There are two kinds of source, and the difference matters:
 *
 *   - `file`: a `File` the user picked or dropped. These are disk-backed path
 *     references, so holding one per instance for the whole session is free
 *     (thousands of them cost a few hundred KB, not the file contents).
 *
 *   - `opfs`: bytes we were handed in memory — a DICOMweb retrieval, which
 *     arrives as an ArrayBuffer with no file behind it. Holding those for the
 *     session would mean carrying the entire study on the heap, which is exactly
 *     what the rest of this design exists to avoid. So they get spooled to OPFS
 *     on arrival and the buffer is dropped; `readBytesByUrl` reads them back.
 *
 * Everything downstream (`instance-source`, the `dicomfile:` loaders, the export
 * writer) goes through `readBytesByUrl` and never learns which kind it got.
 *
 * Owns imageId minting so an id can't exist without a source behind it. The id
 * format matches what WADO's `parseImageId` expects: `dicomfile:<index>`, with
 * frames addressed as `dicomfile:<index>?frame=<n>`. `parseImageId` returns the
 * bare index as `url`, which is the key used here and by `instance-source`.
 */

import * as opfsStore from "../opfs/opfs-store";
import { recordSourceRegistered } from "../session-stats";

/** Thrown when a source can no longer be read (moved, deleted, or evicted). */
export class SourceUnavailableError extends Error {
  constructor(url: string, name: string | undefined, spooled: boolean, cause: unknown) {
    const subject = `the original data${name ? ` for "${name}"` : ""} (${url})`;
    super(
      spooled
        ? `Can't read ${subject}. Its spooled copy is gone — browser storage may ` +
            `have been cleared or run out of space. Re-import and try again.`
        : `Can't read ${subject}. It may have been moved, renamed, or deleted ` +
            `since it was loaded. Re-add the files and try again.`
    );
    this.name = "SourceUnavailableError";
    (this as any).cause = cause;
  }
}

type Source =
  | { kind: "file"; file: File }
  | { kind: "opfs"; key: string; name: string };

/** Namespaced so these can't collide with p10-staging's `p10gz:` keys. */
const KEY_PREFIX = "src";

const sources = new Map<string, Source>();
let nextIndex = 0;

function mint(): string {
  return String(nextIndex++);
}

/** Register a source file and mint its base imageId. */
export function registerFile(file: File): string {
  const url = mint();
  sources.set(url, { kind: "file", file });
  recordSourceRegistered("file", file.size);
  return `dicomfile:${url}`;
}

/**
 * Register bytes we hold in memory, spooling them to OPFS so the caller can drop
 * its reference immediately.
 *
 * Never throws: if OPFS is unavailable, in a write-failure backoff, or out of
 * quota, this falls back to holding the bytes in a memory-backed `File` — the
 * behavior we'd have had anyway, so a storage problem degrades memory use
 * instead of failing the import. The caller can always treat the returned
 * imageId as valid.
 */
export async function registerBytes(
  bytes: ArrayBuffer,
  name: string
): Promise<string> {
  const url = mint();
  const key = `${KEY_PREFIX}:${url}`;

  let spooled = false;
  try {
    spooled = await opfsStore.writeBytes(key, bytes);
  } catch {
    // writeBytes already records and backs off; treat anything escaping it as a
    // plain "couldn't spool".
    spooled = false;
  }

  if (spooled) {
    sources.set(url, { kind: "opfs", key, name });
  } else {
    sources.set(url, { kind: "file", file: new File([bytes], name) });
  }
  recordSourceRegistered(spooled ? "opfs" : "memory", bytes.byteLength);

  return `dicomfile:${url}`;
}

export function hasUrl(url: string): boolean {
  return sources.has(url);
}

/**
 * Re-read a source's bytes. Every cache miss on a `dicomfile:` frame lands here
 * eventually, so this is the hot path for the viewer and the export writer — but
 * `instance-source` keeps the parsed result hot across a whole instance, so in
 * practice this runs once per instance, not once per frame.
 */
export async function readBytesByUrl(url: string): Promise<ArrayBuffer> {
  const source = sources.get(url);
  if (!source) {
    throw new SourceUnavailableError(url, undefined, false, null);
  }

  if (source.kind === "file") {
    try {
      return await source.file.arrayBuffer();
    } catch (err) {
      // NotReadableError: the file changed on disk after the handle was taken.
      throw new SourceUnavailableError(url, source.file.name, false, err);
    }
  }

  let bytes: ArrayBuffer | null;
  try {
    bytes = await opfsStore.readBytes(source.key);
  } catch (err) {
    throw new SourceUnavailableError(url, source.name, true, err);
  }
  // readBytes returns null rather than throwing for a missing or undecryptable
  // file, and there is no other copy of these bytes — so null is fatal here.
  if (!bytes) {
    throw new SourceUnavailableError(url, source.name, true, null);
  }
  return bytes;
}

/**
 * Drop a single source, deleting its spooled copy.
 *
 * For instances that never make it into the case — a file that won't parse, or
 * one rejected by the quarantine check. Registration has to happen before either
 * of those is known (the bytes are what we parse), so without this a study full
 * of rejects would hold OPFS quota all session for instances nobody can see.
 *
 * Never call this for an instance that made it in: its frames are decoded from
 * here on demand for the rest of the session.
 */
export function release(url: string): void {
  const source = sources.get(url);
  if (!source) return;
  sources.delete(url);
  if (source.kind === "opfs") void opfsStore.deleteBytes(source.key);
}

/**
 * Drop every registered source, deleting spooled copies from OPFS. Only for a
 * full session reset — the registry must outlive the viewer and the export
 * phase, since both re-read from it on demand.
 */
export function releaseAll(): void {
  const keys: string[] = [];
  for (const source of sources.values()) {
    if (source.kind === "opfs") keys.push(source.key);
  }
  sources.clear();
  nextIndex = 0;
  // Best-effort and deliberately not awaited: the registry is logically empty
  // the moment it's cleared, and a slow OPFS delete must not hold up a reset.
  for (const key of keys) void opfsStore.deleteBytes(key);
}

export function count(): number {
  return sources.size;
}
